import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  UserGroupIcon,
  ArrowRightIcon,
  CalendarIcon,
  MapPinIcon,
  ShieldCheckIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const FeatureHola = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Connect — Meet Neighbors Over Dining Events | LOCU"
        description="Join community dining events at local restaurants. Meet verified neighbors over shared meals in groups of 4-8. No networking, just good food and conversation."
        path="/feature/hola"
        keywords="community dining events, meet neighbors, social dining, group dining events"
      />
      {/* Hero Section - Black */}
      <div className="bg-black text-white py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Meet your neighbors.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">
              Over great food.
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-400 max-w-2xl mx-auto mb-12">
            Connect with interesting people in your neighborhood over shared meals.
            No networking. No agenda. Just good food & conversation.
          </p>
          {!isAuthenticated && (
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-semibold text-lg hover:bg-gray-100 transition"
            >
              Join Connect
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="text-6xl font-bold text-black mb-2">5km</div>
              <div className="text-lg text-gray-600">Neighborhood</div>
              <div className="text-sm text-gray-400 mt-2">Meet people nearby</div>
            </div>
            <div className="text-center p-8 border-l border-r border-gray-200">
              <div className="text-6xl font-bold text-black mb-2">4-8</div>
              <div className="text-lg text-gray-600">Group Size</div>
              <div className="text-sm text-gray-400 mt-2">Intimate gatherings</div>
            </div>
            <div className="text-center p-8">
              <div className="text-6xl font-bold text-black mb-2">ID</div>
              <div className="text-lg text-gray-600">Verified</div>
              <div className="text-sm text-gray-400 mt-2">Members are rent-verified</div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Connect */}
      <div className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            Why Connect?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPinIcon className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Same Building, New Friends</h3>
              <p className="text-gray-600">Live nearby but never met? Connect fixes that.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-6">
                <ShieldCheckIcon className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Safe & Verified</h3>
              <p className="text-gray-600">Every member is verified. Safety features built-in.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-6">
                <HeartIcon className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold mb-3">Genuine Connections</h3>
              <p className="text-gray-600">No networking. No agenda. Just good food & conversation.</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            How It Works
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Browse Events</h3>
              <p className="text-gray-600">Find dining events at restaurants near you.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Join or Host</h3>
              <p className="text-gray-600">Join an event or create your own at a favorite spot.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">Meet & Eat</h3>
              <p className="text-gray-600">Show up, share a meal, and make new friends.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                4
              </div>
              <h3 className="text-xl font-bold mb-3">Stay Connected</h3>
              <p className="text-gray-600">Build lasting friendships in your neighborhood.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Value Prop */}
      <div className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            New to the neighborhood?
          </h2>
          <p className="text-xl text-gray-600">
            Connect helps you meet verified local residents over shared meals.
            No awkward networking - just good food and genuine conversation.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-black text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Meet your neighbors today
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Join a community of friendly neighbors who love great food.
          </p>
          {!isAuthenticated ? (
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-semibold text-lg hover:bg-gray-100 transition"
            >
              Sign Up to Connect
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={() => navigate('/dining-events')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-semibold text-lg hover:bg-gray-100 transition"
            >
              Find Events
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeatureHola;
